import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  List,
  Datagrid,
  TextField,
  EmailField,
  TopToolbar,
  CreateButton,
  Filter,
  SearchInput,
  SelectInput,
  BooleanInput,
  useListContext,
} from 'react-admin';
import { Chip } from '@material-ui/core';

import useDocumentTitle from '../../hooks/useDocumentTitle';
import {
  ORIGINE_CHOICES,
  ORIGINE_LABELS,
  ORIGINE_VIVIER_CONSTRUIT,
  ORIGINE_NON_CONFORME,
} from '../../helpers';

// Dedicated vivier list (plan 013). Reads the read-only care.person_vivier view
// (resource `person_vivier`): one row per matricule, with an `origine` column
// ('vivier_construit' | 'ex_cdd_rappelable' | 'non_conforme'). The PostgREST
// path = the view name.
//
// `non_conforme` rows (agents carrying an active warning) are HIDDEN by default
// (filterDefaultValues injects `origine=neq.non_conforme`, cf. the PostgREST
// `neq` operator). The "Afficher les non conformes" toggle drops that exclusion.

// Synthetic filter key carrying the PostgREST `neq` operator (see
// ra-data-postgrest `operators.neq`): `neq(origine)=non_conforme` becomes
// `origine=neq.non_conforme`.
const EXCLUDE_NON_CONFORME_KEY = 'neq(origine)';
// Synthetic, UI-only toggle. Must never reach the data provider as a filter
// (no such column on the view) — it is translated into the neq operator below.
const SHOW_NON_CONFORME_KEY = 'show_non_conforme';

const VivierFilter = (props) => {
  return (
    <Filter {...props}>
      <SearchInput source="search" alwaysOn />
      <SelectInput
        source="origine"
        label="Origine"
        choices={ORIGINE_CHOICES}
        alwaysOn
        emptyText="Toutes"
      />
      <BooleanInput
        source={SHOW_NON_CONFORME_KEY}
        label="Afficher les non conformes"
        alwaysOn
      />
    </Filter>
  );
};

// Reconciles the UI-only toggle / origine select with the PostgREST `neq`
// exclusion, and strips the synthetic toggle key before it can hit the API.
// Three cases drop the default `origine=neq.non_conforme` exclusion:
//   - the user ticks "Afficher les non conformes", OR
//   - the user explicitly filters on origine=non_conforme (otherwise the neq +
//     eq would cancel each other out and return nothing).
const NonConformeFilterSync = ({ children }) => {
  const { filterValues, displayedFilters, setFilters } = useListContext();

  const showToggle = !!(filterValues && filterValues[SHOW_NON_CONFORME_KEY]);
  const selectedOrigine = filterValues && filterValues.origine;
  const explicitNonConforme = selectedOrigine === ORIGINE_NON_CONFORME;
  const excludeApplied =
    !!filterValues &&
    filterValues[EXCLUDE_NON_CONFORME_KEY] === ORIGINE_NON_CONFORME;

  const shouldExclude = !showToggle && !explicitNonConforme;

  useEffect(() => {
    if (!filterValues) {
      return;
    }
    if (shouldExclude === excludeApplied) {
      return;
    }
    const next = { ...filterValues };
    if (shouldExclude) {
      next[EXCLUDE_NON_CONFORME_KEY] = ORIGINE_NON_CONFORME;
    } else {
      delete next[EXCLUDE_NON_CONFORME_KEY];
    }
    setFilters(next, displayedFilters, false);
  }, [shouldExclude, excludeApplied]); // eslint-disable-line react-hooks/exhaustive-deps

  return children;
};

NonConformeFilterSync.propTypes = {
  children: PropTypes.node,
};

NonConformeFilterSync.defaultProps = {
  children: null,
};

// Coloured badge for the `origine` column.
const OrigineField = ({ record }) => {
  const origine = record && record.origine;
  if (!origine) {
    return null;
  }
  const label = ORIGINE_LABELS[origine] || origine;
  let color = 'default';
  if (origine === ORIGINE_VIVIER_CONSTRUIT) {
    color = 'primary';
  } else if (origine === ORIGINE_NON_CONFORME) {
    color = 'secondary';
  }
  return <Chip size="small" label={label} color={color} />;
};

OrigineField.propTypes = {
  record: PropTypes.object,
};

OrigineField.defaultProps = {
  record: {},
};

// Clickable `tel:` link for the mobile number. Stops row-click propagation so
// dialing doesn't also navigate to the show view.
const MobilePhoneField = ({ record }) => {
  const phone = record && record.mobile_phone;
  if (!phone) {
    return null;
  }
  return (
    <a
      href={`tel:${phone}`}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {phone}
    </a>
  );
};

MobilePhoneField.propTypes = {
  record: PropTypes.object,
};

MobilePhoneField.defaultProps = {
  record: {},
};

const VivierListActions = ({ basePath }) => {
  return (
    <TopToolbar>
      {/* Create goes through the normal cleaner path, NOT the view. */}
      <CreateButton basePath={basePath} label="Nouveau vivier" />
    </TopToolbar>
  );
};

VivierListActions.propTypes = {
  basePath: PropTypes.string,
};

VivierListActions.defaultProps = {
  basePath: '/person_vivier',
};

const VivierList = (props) => {
  useDocumentTitle('Vivier');
  return (
    <List
      {...props}
      filters={<VivierFilter />}
      filterDefaultValues={{ [EXCLUDE_NON_CONFORME_KEY]: ORIGINE_NON_CONFORME }}
      actions={<VivierListActions />}
      bulkActionButtons={false}
      perPage={25}
      sort={{ field: 'fullname', order: 'ASC' }}
    >
      <NonConformeFilterSync>
        <Datagrid rowClick="show">
          <TextField source="fullname" label="Nom" />
          <OrigineField label="Origine" sortBy="origine" />
          <TextField source="silae_id" label="Id Silae" />
          <EmailField source="email" label="E-mail" />
          <MobilePhoneField
            source="mobile_phone"
            label="Mobile"
            sortBy="mobile_phone"
          />
          <TextField source="mailing_city" label="Ville" />
        </Datagrid>
      </NonConformeFilterSync>
    </List>
  );
};

export default VivierList;
